import React, { type ReactNode } from 'react';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import { useFeatures } from 'flagged';
import Loader from '@/components/common/Loader';
import useOrgContext from '@/lib/hooks/swr/useOrgContext';
import useCurrentUser from '@/lib/hooks/useCurrentUser';
import useOwnerOrgId from '@/lib/hooks/useOwnerOrgId';
import { userService } from '@/services/user';

function MigrationGateContainer({ children }: { children: ReactNode }) {
  return (
    <div
      data-theme="v2"
      className="relative flex h-screen min-h-full flex-col justify-center overflow-hidden before:absolute before:left-[20%] before:-top-[55%] before:z-0 before:h-[80%] before:w-full before:min-w-[700px] before:[background:radial-gradient(50%_50%_at_50%_50%,_#0DB3BF_0%,_#FFF_100%)] before:[mix-blend-mode:multiply] after:absolute after:-left-[20%] after:-bottom-[60%] after:z-0 after:h-full after:w-full after:min-w-[700px] after:[mix-blend-mode:multiply] after:[background:radial-gradient(50%_50%_at_50%_50%,_#CABEFD_0%,_#FFF_100%)]"
    >
      <div className="z-10 mx-auto flex h-full flex-col justify-center overflow-y-auto p-4 text-center">
        {children}
      </div>
    </div>
  );
}
export default function MigrationGate({ children }: { children: ReactNode }) {
  const ownerOrgId = useOwnerOrgId();
  const features = useFeatures();
  const { isSuperAdmin } = useCurrentUser();
  const { data } = useOrgContext(ownerOrgId, {});

  if (isSuperAdmin) {
    return children;
  }

  /**
   * Wait until we have the data before we show any of the UI below this.
   * It will allow for not showing a flash of the `children` UI while the
   * feature flags are being fetched.
   */
  if (!data && userService.isAuthenticated()) {
    return <Loader isCentered className="h-screen items-center" />;
  }

  if (features.migrationCompleted) {
    return (
      <MigrationGateContainer>
        <p className="mb-4 max-w-[1100px] text-[clamp(24px,12vw,75px)] font-semibold text-primary-main">
          Your Instil experience has been upgraded
        </p>

        <p className="mb-2 text-[24px] font-semibold text-text-primary">
          Click the link below to access your Instil account.
        </p>

        <p className="mb-10 text-text-secondary">
          Please note that if it’s your first time using the upgraded platform
          you will need to reset your password
        </p>

        <Button
          href="https://app.instil.io"
          component={Link}
          variant="contained"
          className="place-self-center hover:text-white active:text-white"
        >
          Go To New Instil
        </Button>
      </MigrationGateContainer>
    );
  }

  if (features.migrationStarted) {
    return (
      <MigrationGateContainer>
        <p className="text-text-secondary">Site down for maintenance</p>

        <p className="mb-4 text-[clamp(24px,12vw,75px)] font-semibold text-primary-main">
          Upgrading to New Features
        </p>

        <p className="text-[24px] font-semibold text-text-primary">
          We can&#39;t wait for you to try it!
        </p>
      </MigrationGateContainer>
    );
  }

  return children;
}
